import { create } from 'zustand'
import { env } from '@/config/env'
import { DEV_AUTH_SENTINEL, isDevAuthBypass } from '@/lib/devAuth'
import { useRuntimeStore } from '@/store/useRuntimeStore'
import { useUserStore } from '@/store/useUserStore'
import { bridge } from '@/utils/bridge'
import { TOKEN_KEY, USER_ID_KEY, USER_NAME_KEY, storage } from '@/utils/storage'

interface AuthState {
  token: string | null
  userId: string | null
  name: string | null
  ready: boolean
  /** 客户端宿主鉴权失败（可重试），与「浏览器未登录」区分 */
  hostAuthFailed: boolean
  init: () => Promise<void>
  /** 401 或用户点重试：再向宿主要一次 JWT */
  refreshFromHost: () => Promise<boolean>
  setSession: (session: { token: string; userId?: string | null; name?: string | null }) => void
  logout: () => void
}

let offOrgSwitch: (() => void) | null = null

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return null
  }
}

function isTokenExpired(token: string): boolean {
  const payload = parseJwtPayload(token)
  // 非 JWT / 解析失败：无法判断过期，保留 token
  if (!payload) return false
  const expiry = payload.expiry ?? payload.exp
  if (typeof expiry !== 'number') return false
  return expiry < Date.now() / 1000
}

function readStoredAuth(): { token: string | null; userId: string | null; name: string | null } {
  let token = storage.get(TOKEN_KEY)
  if (token && isTokenExpired(token)) {
    storage.remove(TOKEN_KEY)
    token = null
  }
  const userId = storage.get(USER_ID_KEY) || (token ? readUserId(token) : null)
  return { token, userId, name: storage.get(USER_NAME_KEY) }
}

function readUserId(token: string): string | null {
  const payload = parseJwtPayload(token)
  const uid = payload?.user_id ?? payload?.sub
  return typeof uid === 'string' && uid ? uid : null
}

function ensureDevUser() {
  const current = useUserStore.getState()
  if (current.user && current.isAuthenticated) return
  current.enter({ userId: current.user?.id ?? '', space_id: current.space_id || '' })
}

function hydrateUser(userId: string, name?: string | null, spaceId?: string | null) {
  const current = useUserStore.getState()
  const space_id = spaceId || current.space_id || env.devOrgId || ''
  if (!current.user || current.user.id !== userId || !current.isAuthenticated) {
    current.enter({ userId, space_id })
  } else if (spaceId && current.space_id !== spaceId) {
    current.setSpaceId(spaceId)
  }
  if (name) current.updateProfile({ name })
}

function applyHostAuth(
  set: (partial: Partial<AuthState>) => void,
  get: () => AuthState,
  auth: NonNullable<Awaited<ReturnType<typeof bridge.getAuth>>>,
) {
  get().setSession({
    token: auth.jwt,
    userId: auth.userId || null,
    name: auth.userName || null,
  })
  if (auth.orgId) useUserStore.getState().setSpaceId(auth.orgId)
  offOrgSwitch?.()
  offOrgSwitch = bridge.onOrgSwitch(({ orgId }) => {
    useUserStore.getState().setSpaceId(orgId)
  })
  set({ hostAuthFailed: false, ready: true })
}

function createInitialAuth(): Pick<
  AuthState,
  'token' | 'userId' | 'name' | 'ready' | 'hostAuthFailed'
> {
  if (isDevAuthBypass()) {
    return {
      token: DEV_AUTH_SENTINEL,
      userId: null,
      name: null,
      ready: true,
      hostAuthFailed: false,
    }
  }
  const stored = readStoredAuth()
  const waitingForHost = useRuntimeStore.getState().isClient && !stored.token
  return { ...stored, ready: !waitingForHost, hostAuthFailed: false }
}

const initialAuth = createInitialAuth()

export const useAuthStore = create<AuthState>((set, get) => ({
  token: initialAuth.token,
  userId: initialAuth.userId,
  name: initialAuth.name,
  ready: initialAuth.ready,
  hostAuthFailed: initialAuth.hostAuthFailed,

  async init() {
    if (isDevAuthBypass()) {
      ensureDevUser()
      set({
        token: DEV_AUTH_SENTINEL,
        userId: useUserStore.getState().user?.id,
        ready: true,
        hostAuthFailed: false,
      })
      return
    }

    if (useRuntimeStore.getState().isClient) {
      const auth = await bridge.getAuth()
      if (auth?.jwt) {
        applyHostAuth(set, get, auth)
        return
      }
      // 宿主暂时拿不到 JWT：保留本地未过期会话，避免误清后白屏/失败页
      const stored = readStoredAuth()
      if (stored.token) {
        set({ ...stored, ready: true, hostAuthFailed: false })
        if (stored.userId) hydrateUser(stored.userId, stored.name)
        return
      }
      set({ token: null, userId: null, name: null, ready: true, hostAuthFailed: true })
      return
    }

    const stored = readStoredAuth()
    set({ ...stored, ready: true, hostAuthFailed: false })
    if (stored.token && stored.userId) hydrateUser(stored.userId, stored.name)
  },

  async refreshFromHost() {
    const auth = await bridge.getAuth()
    if (!auth?.jwt) {
      set({ hostAuthFailed: true, ready: true })
      return false
    }
    applyHostAuth(set, get, auth)
    return true
  },

  setSession({ token, userId, name }) {
    storage.set(TOKEN_KEY, token)
    const resolvedUserId = userId || readUserId(token)
    if (resolvedUserId) storage.set(USER_ID_KEY, resolvedUserId)
    if (name) storage.set(USER_NAME_KEY, name)
    set({
      token,
      userId: resolvedUserId,
      name: name ?? get().name,
      ready: true,
      hostAuthFailed: false,
    })
    if (resolvedUserId) hydrateUser(resolvedUserId, name)
  },

  logout() {
    storage.remove(TOKEN_KEY)
    storage.remove(USER_ID_KEY)
    storage.remove(USER_NAME_KEY)
    offOrgSwitch?.()
    offOrgSwitch = null
    if (isDevAuthBypass()) {
      ensureDevUser()
      set({
        token: DEV_AUTH_SENTINEL,
        userId: useUserStore.getState().user?.id ?? null,
        name: null,
        ready: true,
        hostAuthFailed: false,
      })
      return
    }
    useUserStore.getState().logout()
    set({
      token: null,
      userId: null,
      name: null,
      ready: true,
      hostAuthFailed: useRuntimeStore.getState().isClient,
    })
  },
}))
